/**
 * curated-first.js
 *
 * Pins one product to the first slot of a collection grid, so an ad that showed
 * a specific design lands on a browsable collection with that design first.
 *
 *   /collections/prints?first=he-still-hears
 *   /collections/prints?first=he-still-hears&variant=53946695123285
 *
 * `first` also accepts a full (URL-encoded) product URL, which is what Meta's
 * catalog deep-link override passes:
 *
 *   ?first=https%3A%2F%2Fshop.faithfulgrace.com%2Fproducts%2Fhe-still-hears%3Fvariant%3D123
 *
 * If the product is already rendered on the page it is moved, costing no request.
 * Otherwise it is fetched via the product.card alternate template and prepended.
 *
 * Fails silently and leaves the page untouched on any error.
 */
(function () {
  'use strict';

  var GRID_SELECTOR = '#product-grid';

  function handleFromParam(raw) {
    if (!raw) return null;
    var value = raw.trim();

    // Meta passes the whole product URL, sometimes double-encoded.
    for (var i = 0; i < 2 && value.indexOf('%') !== -1; i++) {
      try {
        value = decodeURIComponent(value);
      } catch (e) {
        break;
      }
    }

    var match = value.match(/\/products\/([^/?#]+)/);
    if (match) return match[1];

    // Bare handle. Reject anything that still looks like a URL or path.
    if (/^[a-z0-9][a-z0-9-]*$/i.test(value)) return value;
    return null;
  }

  function findExistingCard(grid, handle) {
    var link = grid.querySelector('a[href*="/products/' + handle + '"]');
    if (!link) return null;

    // Only treat it as a match if the handle is exact, not a prefix of another.
    var href = link.getAttribute('href') || '';
    var matched = href.match(/\/products\/([^/?#]+)/);
    if (!matched || matched[1] !== handle) return null;

    return link.closest('li.grid__item');
  }

  function applyVariant(item, variantId) {
    if (!variantId) return;
    item.querySelectorAll('a[href*="/products/"]').forEach(function (anchor) {
      try {
        var url = new URL(anchor.getAttribute('href'), window.location.origin);
        url.searchParams.set('variant', variantId);
        anchor.setAttribute('href', url.pathname + url.search);
      } catch (e) {
        /* leave the link as-is */
      }
    });
  }

  function pin(grid, item, variantId) {
    applyVariant(item, variantId);
    item.setAttribute('data-curated-first', 'true');
    // Dawn staggers reveal animations by --animation-order; reset so the pinned
    // card does not animate in last.
    item.style.setProperty('--animation-order', '1');
    grid.insertBefore(item, grid.firstElementChild);
  }

  function run() {
    var grid = document.querySelector(GRID_SELECTOR);
    if (!grid || grid.tagName !== 'UL') return;

    var params = new URLSearchParams(window.location.search);
    var handle = handleFromParam(params.get('first'));
    if (!handle) return;

    var variantId = params.get('variant');

    var existing = findExistingCard(grid, handle);
    if (existing) {
      pin(grid, existing, variantId);
      return;
    }

    fetch('/products/' + encodeURIComponent(handle) + '?view=card', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('card fetch failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var item = parsed.querySelector('li.grid__item');
        if (!item) throw new Error('no card in response');

        // Guard against a redirect or error page rendering something unrelated.
        if (!findCardHandle(item, handle)) throw new Error('card handle mismatch');

        pin(grid, document.importNode(item, true), variantId);
      })
      .catch(function () {
        /* leave the collection exactly as the server rendered it */
      });
  }

  function findCardHandle(item, handle) {
    var link = item.querySelector('a[href*="/products/' + handle + '"]');
    if (!link) return false;
    var matched = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return !!matched && matched[1] === handle;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
